import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import styled from "styled-components";

import { CONTROL_VISIBILITY_TIMEOUTS } from "../../constants";
import { useEmbedParametersContext } from "../../contexts/embedParametersContext";
import { useMediaContext } from "../../contexts/MediaContext";
import { usePlayerContext } from "../../contexts/playerContext";
import { VolumeChangedContext } from "../../contexts/VolumeChangedContext";
import * as Enums from "../../enums";
import { useControlsVisibility } from "../../hooks/useControlsVisibility/useControlsVisibility";
import { usePlayerStore } from "../../store/usePlayerStore";
import { focusColor } from "../../styles/variables";
import ChromecastOverlay from "../Chromecast/ChromecastOverlay";
import { ErrorOverlay } from "../Error/ErrorOverlay/ErrorOverlay";
import NextEpisodeOverlay from "../NextEpisodeOverlay";
import { ReplayButton } from "../PlayButton/ReplayButton";
import Recommendations from "../recommendations";
import ShareOverlay from "../share/ShareOverlay";
import { SkipButton } from "../skipButton/SkipButton";
import { TimeLeft } from "../TimeLeft";

import { ActionIndicator } from "./ActionIndicator";
import { BottomControlBar } from "./BottomControlBar";
import { DoubleTaps } from "./DoubleTap";
import { toggleFullscreen } from "./Fullscreen.util";
import { MobileOverlayControls } from "./MobileOverlayControls";
import { OverlayControls } from "./OverlayControls";
import { togglePictureInPicture } from "./PictureInPicture";
import { Subtitles } from "./Subtitles";
import { TopControlBar } from "./TopControlBar";
import { UnmuteButton } from "./UnmuteButton";

const UI = styled.div<{
  showCursor: boolean;
  casting?: boolean;
}>`
  background-color: ${({ casting }) => (casting ? "black" : "")};
  bottom: 0;
  cursor: ${({ showCursor }) => (showCursor ? "auto" : "none")};
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: absolute;
  width: 100%;

  &:focus {
    outline: none !important;
  }

  &:focus-visible {
    outline: 1px solid ${focusColor} !important;
  }
`;

export const PlayerUI = ({
  uiRef
}: {
  uiRef: MutableRefObject<HTMLDivElement | null>;
  // eslint-disable-next-line complexity
}) => {
  const { getState, useStoreState } = usePlayerStore();
  const device = useStoreState.device();
  const durationInSeconds = useStoreState.durationInSeconds();
  const isCasting = useStoreState.isCasting();
  const isControlsVisible = useStoreState.isControlsVisible();
  const isDraggingTimeline = useStoreState.isDraggingTimeline();
  const isEnded = useStoreState.isEnded();
  const isFullscreen = useStoreState.isFullscreen();
  const isLive = useStoreState.isLive();
  const isNativeSubtitles = useStoreState.isNativeSubtitles();
  const isPaused = useStoreState.isPaused();
  const isSimulcast = useStoreState.isSimulcast();
  const player = useStoreState.player();
  const playerElement = useStoreState.playerElement();
  const playerUiType = useStoreState.playerUiType();
  const visibleSettingsMenu = useStoreState.visibleSettingsMenu();

  const [actionIcon, setActionIcon] = useState(null);
  const [shareOverlayOpen, setShareOverlayOpen] = useState<boolean>(false);
  const [recommendationsDisabled, setRecommendationsDisabled] =
    useState<boolean>(false);
  const [volumeSliderVisible, setVolumeSliderVisible] =
    useState<boolean>(false);

  const actionIconTimer = useRef<number>();
  const fresh = useRef<{
    isMobile?: boolean;
    paused?: boolean;
  }>({});
  const muteButton = useRef<HTMLButtonElement | null>(null);
  const quietFocusRef = useRef(false);
  const volumeSliderTimer = useRef<number>();

  const { toggleControls, toggleMobileControls } = useControlsVisibility();

  const {
    autoFocus,
    autoPlayNextEpisode,
    autoplay,
    loop,
    recommendations,
    replayButton,
    sharing,
    subtitles: subtitlesEnabled,
    timeLeft
  } = useEmbedParametersContext();

  const { update } = usePlayerContext();

  const { cuepoints, isAreenaVisible, isContentProtected, nextEpisode } =
    useMediaContext();

  fresh.current = {
    isMobile: device.isMobile,
    paused: isPaused
  };

  const togglePlayback = useCallback(() => {
    if (fresh.current.paused) {
      showActionIcon("play");
      player.play();
    } else {
      showActionIcon("pause");
      player.pause();
    }
  }, [player]);

  const overlayControlsClick = useCallback(() => {
    if (device.isMobile) {
      toggleMobileControls(undefined);
    } else {
      togglePlayback();
    }
  }, [device, toggleMobileControls, togglePlayback]);

  const overlayControlsDoubleClick = useCallback(() => {
    if (!device.isMobile) {
      toggleFullscreen(playerElement, player);
    }
  }, [device, player, playerElement]);

  const keyboardVolumeChange = useCallback(
    increase => {
      const currentVolume = getState().volume;
      const newVolume = +Math.min(
        1,
        Math.max(0, (currentVolume || 0) + (increase ? 0.05 : -0.05))
      ).toFixed(3);

      if (newVolume !== currentVolume) {
        player.setVolume(newVolume, true);
      }

      setVolumeSliderVisible(true);

      clearTimeout(volumeSliderTimer.current);

      volumeSliderTimer.current = setTimeout(() => {
        setVolumeSliderVisible(false);
      }, CONTROL_VISIBILITY_TIMEOUTS.KEYBOARD) as unknown as number;
    },
    [getState, player]
  );

  const showActionIcon = icon => {
    clearTimeout(actionIconTimer.current);

    setActionIcon(null);
    setTimeout(() => {
      setActionIcon(icon);
    });

    actionIconTimer.current = setTimeout(() => {
      setActionIcon(null);
    }, 900) as unknown as number;
  };

  useEffect(
    () => () => {
      clearTimeout(actionIconTimer.current);
    },
    []
  );

  const handleKeyDown = useCallback(
    event => {
      if (!shareOverlayOpen && !visibleSettingsMenu) {
        event.preventDefault();
      }
    },
    [shareOverlayOpen, visibleSettingsMenu]
  );

  const onKeyDown = useCallback(
    event => {
      switch (event.key) {
        case "ArrowUp":
          if (shareOverlayOpen) {
            break;
          }
          event.preventDefault();
          keyboardVolumeChange(true);
          break;

        case "ArrowDown":
          if (shareOverlayOpen) {
            break;
          }
          event.preventDefault();
          keyboardVolumeChange(false);
          break;

        case "ArrowLeft":
        case "ArrowRight":
        case " ":
          handleKeyDown(event);
      }
    },
    [shareOverlayOpen, keyboardVolumeChange, handleKeyDown]
  );

  const toggleMute = useCallback(() => {
    const isMuted = getState().isMuted;
    showActionIcon(isMuted ? "unmute" : "mute");
    player.setMuted(!isMuted, true);
  }, [getState, player]);

  const handleSpaceKey = useCallback(() => {
    if (shareOverlayOpen || visibleSettingsMenu !== undefined) {
      return;
    }
    togglePlayback();
  }, [shareOverlayOpen, togglePlayback, visibleSettingsMenu]);

  const handleArrowRight = useCallback(() => {
    if (shareOverlayOpen || getState().isEnded) {
      return;
    }
    player.seekFor(10);
    showActionIcon("forward");
  }, [getState, player, shareOverlayOpen]);

  const handleArrowLeft = useCallback(() => {
    if (shareOverlayOpen || getState().currentTime === 0) {
      return;
    }
    player.seekFor(-10);
    showActionIcon("back");
  }, [getState, player, shareOverlayOpen]);

  const onKeyUp = useCallback(
    event => {
      switch (event.key.toLowerCase()) {
        case "arrowleft":
          handleArrowLeft();
          break;

        case "arrowright":
          handleArrowRight();
          break;

        case "f":
          toggleFullscreen(playerElement, player);
          break;

        case "i":
          togglePictureInPicture(player);
          break;

        case "m":
          toggleMute();
          break;

        case "k":
          togglePlayback();
          break;
        case " ":
          handleSpaceKey();
      }
    },
    [
      handleArrowLeft,
      handleArrowRight,
      handleSpaceKey,
      player,
      playerElement,
      toggleMute,
      togglePlayback
    ]
  );

  useEffect(() => {
    if (autoFocus) {
      uiRef.current?.focus();
    }
  }, [autoFocus, uiRef]);

  useEffect(() => {
    if (uiRef.current) {
      const uiElement = uiRef.current as HTMLDivElement;

      const listener = () => {
        quietFocusRef.current = true;
        uiElement.focus();
      };

      uiElement.addEventListener("quietFocus", listener);
      return () => {
        uiElement.removeEventListener("quietFocus", listener);
      };
    }
  }, [uiRef]);

  const onControlsFocus = event => {
    if (!muteButton.current?.contains(event.target) && !quietFocusRef.current) {
      toggleControls(true);
    }
    quietFocusRef.current = false;

    toggleControls(false, CONTROL_VISIBILITY_TIMEOUTS.MOVE_HOVER);
  };
  const onMouseOut = () => {
    toggleControls(false, CONTROL_VISIBILITY_TIMEOUTS.OUT_HOVER);
  };

  useEffect(() => {
    if (playerElement) {
      const onDocumentClick = event => {
        if (!uiRef.current?.contains(event.target)) {
          toggleControls(false);
        }
      };

      if (device.isMobile) {
        document.addEventListener("click", onDocumentClick, true);
      }
      return () => {
        if (device.isMobile) {
          document.removeEventListener("click", onDocumentClick, true);
        }
      };
    }
  }, [device, playerElement, toggleControls, uiRef]);

  const shouldRenderRecommendations =
    !loop &&
    recommendations &&
    isAreenaVisible &&
    !isSimulcast &&
    !isCasting &&
    durationInSeconds;

  const draggingTimelineOnMobile = device.isMobile && isDraggingTimeline;

  const shouldRenderShareButton =
    sharing && isAreenaVisible && !isSimulcast && !draggingTimelineOnMobile;

  const closeShareOverlay = useCallback(() => {
    setRecommendationsDisabled(false);
    setShareOverlayOpen(false);
  }, []);

  const openShareOverlay = useCallback(() => {
    setRecommendationsDisabled(true);
    setShareOverlayOpen(true);
  }, []);

  const showNextEpisodeOverlay =
    !loop && nextEpisode && durationInSeconds && autoPlayNextEpisode;

  return (
    <UI
      casting={isCasting}
      onClick={onControlsFocus}
      onFocus={onControlsFocus}
      onInput={onControlsFocus}
      onMouseMove={device.isMobile ? undefined : onControlsFocus}
      onMouseOut={onMouseOut}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      ref={uiRef}
      showCursor={
        playerUiType !== Enums.PlayerUiType.default ||
        isControlsVisible ||
        isPaused
      }
      tabIndex={0}>
      {replayButton && !isDraggingTimeline && isEnded && <ReplayButton />}
      <UnmuteButton
        {...{
          autoplay,
          controlsVisible: isControlsVisible,
          ref: muteButton
        }}
      />
      <ActionIndicator icon={actionIcon} />
      {device.isMobile && (
        <DoubleTaps
          onSeekFor={player.seekFor}
          showActionIcon={showActionIcon}
          toggleControls={showControls => toggleMobileControls(showControls)}
        />
      )}
      <OverlayControls
        onClick={overlayControlsClick}
        onDoubleClick={overlayControlsDoubleClick}
      />
      {shouldRenderRecommendations && (
        <Recommendations
          disabled={recommendationsDisabled}
          hasNextEpisode={!!showNextEpisodeOverlay}
          newPlayerUi={true}
          uiRef={uiRef.current}
          update={update}
        />
      )}
      {showNextEpisodeOverlay && (
        <NextEpisodeOverlay
          isAudio={false}
          newPlayerUi={true}
          nextEpisode={nextEpisode}
          update={update}
        />
      )}
      <ErrorOverlay />
      {!draggingTimelineOnMobile && (
        <SkipButton
          buttonType="intro"
          cuepoints={cuepoints}
          UIRef={uiRef.current}
          started={true}
        />
      )}
      {!showNextEpisodeOverlay && !draggingTimelineOnMobile && (
        <SkipButton
          buttonType="outro"
          cuepoints={cuepoints}
          UIRef={uiRef.current}
          started={true}
        />
      )}
      {timeLeft && !isLive && !isFullscreen && <TimeLeft />}
      {playerUiType === Enums.PlayerUiType.default &&
        !draggingTimelineOnMobile && <TopControlBar />}
      {playerUiType === Enums.PlayerUiType.default && device.isMobile && (
        <MobileOverlayControls />
      )}

      {shouldRenderShareButton && !isCasting && (
        <ShareOverlay
          closeShareOverlay={closeShareOverlay}
          isContentProtected={isContentProtected}
          openShareOverlay={openShareOverlay}
        />
      )}

      {isCasting && <ChromecastOverlay />}

      {subtitlesEnabled && !isNativeSubtitles && !isEnded && !isCasting && (
        <Subtitles />
      )}
      {playerUiType === Enums.PlayerUiType.default && (
        <VolumeChangedContext.Provider value={volumeSliderVisible}>
          <BottomControlBar />
        </VolumeChangedContext.Provider>
      )}
    </UI>
  );
};
